import {
    getItem as getLocalItem,
    setItem as setLocalItem,
    removeItem as removeLocalItem,
} from 'owa-local-storage';
import type { MailboxInfo } from 'owa-client-types';
import { getIndexerValueForMailboxInfo } from 'owa-client-types';

/**
 * Per-account localStorage cache that records whether the user has reached
 * the "steady + mirror seen" terminal state in a previous session.
 *
 * When this flag is present at boot, MyOutlookSection can render immediately
 * without waiting for network calls (resumePipeline, loadKnowledgeArtifacts,
 * loadMirrorReveal) to resolve.
 */

const CACHE_KEY_PREFIX = 'owa-personal-os-steady-cached';

function getCacheKey(accountKey: string): `owa-personal-os-steady-cached_${string}` {
    return `${CACHE_KEY_PREFIX}_${accountKey}`;
}

/**
 * Returns true when the previous session recorded this account as having
 * reached the steady+mirror terminal state.
 */
export function hasCachedSteadyState(mailboxInfo: MailboxInfo): boolean {
    const accountKey = getIndexerValueForMailboxInfo(mailboxInfo);
    if (!accountKey) {
        return false;
    }
    return getLocalItem(self, getCacheKey(accountKey)) === 'steady';
}

/**
 * Persist the steady-state marker once the user reaches the
 * steady + mirror-seen state. Call this from the live path after
 * the mirror reveal load confirms data + seen.
 */
export function persistSteadyState(mailboxInfo: MailboxInfo): void {
    const accountKey = getIndexerValueForMailboxInfo(mailboxInfo);
    if (accountKey) {
        setLocalItem(self, getCacheKey(accountKey), 'steady');
    }
}

/**
 * Clear the cached state — called when the user resets onboarding
 * or clears account data.
 */
export function clearCachedSteadyState(mailboxInfo: MailboxInfo): void {
    const accountKey = getIndexerValueForMailboxInfo(mailboxInfo);
    if (accountKey) {
        removeLocalItem(self, getCacheKey(accountKey));
    }
}
